import React, { useState, useEffect } from 'react';
import {
  TextField,
  CircularProgress,
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  Tooltip,
  Stack,
  Checkbox,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  SwapHoriz as TransferIcon,
  ReceiptLong as ReceiptLongIcon,
  LockOpen as ReleaseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import GirviPageShell from '../common/GirviPageShell';
import DetailDialog, {
  DetailHeaderPhotos,
  money,
  listFilterFieldSx,
  listTableHeadSx,
  listActionBtnSx,
} from '../common/DetailDialog';
import PhotoThumb from '../common/PhotoThumb';
import baseURL from '../../baseURL';
import { computeArticleTypeWeightTotals, formatWeightGrams3 } from '../../utils/pledgeArticleWeights';
import { DEFAULT_DHARA, formatDhara } from '../../constants/dhara';
import { pledgeDisplayPhotos } from '../../utils/pledgePhotoUrl';
import { days360, interestTillDate, resolvePledgeDhara, yearlyCompoundRelease } from '../../utils/dharaInterest';
import { markPledgeTicketPrinted, clearTransferToA } from '../../utils/pledgeApi';
import TransferToADialog from './TransferToADialog';
import CreateReceipt from './receipt/CreateReceipt';
import ReleasePage from './ReleasePage';

function PledgeViewCard({ title, items, columns = 2 }) {
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e6ebf1', borderRadius: 1.5, px: 1.5, py: 1.15 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#0f172a', borderLeft: '3px solid #c9a24d', pl: 0.75, mb: 1 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: `repeat(${columns}, minmax(0, 1fr))` },
          columnGap: 2,
          rowGap: 1,
        }}
      >
        {items.map((item) => (
          <Box key={item.label} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', lineHeight: 1.2 }}>
              {item.label}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 750, color: '#0f172a', lineHeight: 1.3, wordBreak: 'break-word' }}>
              {item.value == null || item.value === '' ? '—' : item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function pledgeViewWeightLine(pledge, masterArticles) {
  const typeTotals = computeArticleTypeWeightTotals(pledge?.articles, masterArticles);
  if (typeTotals.length >= 2) {
    return typeTotals.map((t) => `${t.title} gross ${formatWeightGrams3(t.totalGrossWeight)} g`).join(' · ');
  }
  const gross = pledge?.totalGrossWeight
    ? parseFloat(pledge.totalGrossWeight)
    : (pledge?.articles || []).reduce((s, a) => s + parseFloat(a.grossWeight || a.grossWt || 0), 0);
  const net = pledge?.totalNetWeight
    ? parseFloat(pledge.totalNetWeight)
    : (pledge?.articles || []).reduce((s, a) => s + parseFloat(a.netWeight || a.netWt || 0), 0);
  return `Gross ${formatWeightGrams3(gross)} g · Net ${formatWeightGrams3(net)} g`;
}

function pledgeInterestPair(pledge) {
  const simple = interestTillDate(pledge, new Date(), DEFAULT_DHARA);
  const principal = Number(pledge?.loanDetails?.principalAmount) || 0;
  const rate = Number(pledge?.loanDetails?.rateOfInterest) || 0;
  const days = days360(pledge?.date, new Date());
  const compound = yearlyCompoundRelease(principal, rate, days);
  return {
    simple,
    compound: compound ? compound.interest : simple,
    withinYear: days <= 360,
  };
}

const AllPledges = () => {
  const navigate = useNavigate();
  const [pledges, setPledges] = useState([]);
  const [filteredPledges, setFilteredPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState(-1);
  const [selectedPledge, setSelectedPledge] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [interestViewMode, setInterestViewMode] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pledgeToDelete, setPledgeToDelete] = useState(null);
  const [masterArticles, setMasterArticles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferPledgeIds, setTransferPledgeIds] = useState([]);
  const [actionModal, setActionModal] = useState(null);

  useEffect(() => {
    fetchPledges();
  }, []);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${baseURL}/articles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(data)) setMasterArticles(data);
        else if (Array.isArray(data?.data)) setMasterArticles(data.data);
      } catch (e) {
        console.error('Failed to load article master for pledge details:', e);
      }
    };
    fetchArticles();
  }, []);

  const fetchPledges = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/pledges`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      // Log the complete fetch data
      console.log('Fetched Pledges Data:', data);
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch pledges');
      }

      if (data.success) {
        setPledges(data.data || []);
        setFilteredPledges(data.data || []);  
        setSearchTerm('');
        setDateFrom('');
        setDateTo('');
      } else {
        setPledges([]);
        setFilteredPledges([]);
      }
    } catch (error) {
      console.error('Error fetching pledges:', error);
      setPledges([]);
      setFilteredPledges([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    applyFilters();
  };

  const handleDateChange = (type) => (event) => {
    const date = event.target.value;
    if (type === 'from') {
      setDateFrom(date);
    } else {
      setDateTo(date);
    }
    applyFilters();
  };

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const applyFilters = () => {
    let filtered = [...pledges];

    if (searchTerm) {
      const searchRegex = new RegExp(searchTerm, 'i');
      filtered = filtered.filter(pledge => 
        searchRegex.test(pledge.customer?.firstName || '') ||
        searchRegex.test(pledge.customer?.lastName || '') ||
        searchRegex.test(pledge.customer?.code || '') ||
        searchRegex.test(pledge.loanDetails?.ticketNo || '') ||
        searchRegex.test(pledge.customer?.mobileNo || '') ||
        searchRegex.test(pledge.customer?.village || '') ||
        searchRegex.test(String(pledge.loanDetails?.principalAmount || ''))
      );
    }

    if (dateFrom || dateTo) {
      // Normalize dates to start/end of day for accurate comparison
      let fromDate = null;
      let toDate = null;
      
      if (dateFrom) {
        fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
      }
      
      if (dateTo) {
        toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
      }
      
      filtered = filtered.filter(pledge => {
        if (!pledge.date) return false;
        const pledgeDate = new Date(pledge.date);
        // Normalize pledge date to start of day for comparison
        pledgeDate.setHours(0, 0, 0, 0);
        
        if (fromDate && pledgeDate < fromDate) return false;
        if (toDate && pledgeDate > toDate) return false;
        return true;
      });
    }

    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        const aValue = sortField.includes('.') 
          ? sortField.split('.').reduce((obj, key) => obj[key], a)
          : a[sortField];
        const bValue = sortField.includes('.') 
          ? sortField.split('.').reduce((obj, key) => obj[key], b)
          : b[sortField];
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder * aValue.localeCompare(bValue);
        }
        return sortOrder * (aValue - bValue);
      });
    }

    setFilteredPledges(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === -1 ? 1 : -1);
    } else {
      setSortField(field);
      setSortOrder(-1);
    }
    applyFilters();
  };

  const sortMark = (field) =>
    sortField === field ? (sortOrder === -1 ? ' ▼' : ' ▲') : '';

  const handleViewDetails = async (pledge) => {
    setInterestViewMode(pledgeInterestPair(pledge).withinYear ? 'simple' : 'compound');
    setSelectedPledge(pledge);
    setOpenDetails(true);
    // Refresh full pledge so hydrated / latest photos appear in the eye modal
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/${pledge._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.pledge) {
        setSelectedPledge(data.pledge);
        setPledges((prev) => prev.map((p) => (p._id === data.pledge._id ? { ...p, ...data.pledge } : p)));
        setFilteredPledges((prev) => prev.map((p) => (p._id === data.pledge._id ? { ...p, ...data.pledge } : p)));
      }
    } catch (e) {
      console.error('Failed to refresh pledge details:', e);
    }
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const handleEdit = (pledge) => {
    navigate(`/pledge/create?edit=${pledge._id}`);
  };

  const handleDelete = (pledge) => {
    setPledgeToDelete(pledge);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pledgeToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/${pledgeToDelete._id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh the pledges list
        fetchPledges();
        setDeleteConfirmOpen(false);
        setPledgeToDelete(null);
      } else {
        alert(data.message || 'Failed to delete pledge');
      }
    } catch (error) {
      console.error('Error deleting pledge:', error);
      alert('Error deleting pledge');
    }
  };

  const handlePrint = async (pledge) => {
    try {
      const { printDocument, pledgeArticlePrintRate } = await import('../../utils/printUtility');
      const { PledgePrint } = await import('../../components/print/PrintTemplates');
      const articleRate = pledgeArticlePrintRate(masterArticles, pledge);
      const forPrint = articleRate == null
        ? pledge
        : {
            ...pledge,
            loanDetails: { ...(pledge.loanDetails || {}), rateOfInterest: articleRate },
          };
      await printDocument('pledge', forPrint, PledgePrint);
      await markPledgeTicketPrinted(pledge._id);
      const markPrinted = (p) =>
        p._id === pledge._id ? { ...p, ticketPrinted: true } : p;
      setPledges((prev) => prev.map(markPrinted));
      setFilteredPledges((prev) => prev.map(markPrinted));
    } catch (error) {
      console.error('Error printing pledge:', error);
      alert(error.message || 'Error printing document. Please try again.');
    }
  };

  const transferableIds = (ids) =>
    ids.filter((id) => {
      const p = pledges.find((x) => x._id === id);
      return p && !p.transferredToA;
    });

  const toggleSelectPledge = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredPledges
      .filter((p) => !p.transferredToA)
      .map((p) => p._id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const applyTransferResults = (results = []) => {
    const byId = Object.fromEntries(
      results.filter((r) => r.success).map((r) => [String(r.pledgeId), r])
    );
    const patch = (p) => {
      const r = byId[String(p._id)];
      if (!r) return p;
      return {
        ...p,
        transferredToA: true,
        transferredAt: p.transferredAt || new Date().toISOString(),
        vpsATicketNo: r.vpsATicketNo,
        vpsAPledgeId: r.vpsAPledgeId,
      };
    };
    setPledges((prev) => prev.map(patch));
    setFilteredPledges((prev) => prev.map(patch));
    setSelectedIds((prev) => prev.filter((id) => !byId[String(id)]));
  };

  const openTransferDialog = (pledgeIds) => {
    const ids = transferableIds(pledgeIds);
    if (ids.length === 0) {
      alert('No pledges selected for transfer (already transferred pledges are skipped).');
      return;
    }
    setTransferPledgeIds(ids);
    setTransferDialogOpen(true);
  };

  const handleTransferSuccess = (results, { done } = {}) => {
    applyTransferResults(results);
    if (done) {
      fetchPledges();
      const failed = results.filter((r) => !r.success);
      if (failed.length) {
        alert(
          `Some transfers failed:\n${failed
            .map((f) => `${f.ticketNo || f.pledgeId}: ${f.error}`)
            .join('\n')}`
        );
      } else {
        alert('Transfer complete. Pledge remains on VPS B with "Transferred" status.');
      }
      setTransferDialogOpen(false);
      setTransferPledgeIds([]);
    }
  };

  const handleResetTransfer = async (pledge) => {
    if (!pledge?._id) return;
    const ok = window.confirm(
      `Reset transfer status for ticket ${pledge.loanDetails?.ticketNo || '—'}?\n\nUse this if the pledge was deleted on VPS A but still shows as transferred here.`
    );
    if (!ok) return;
    try {
      await clearTransferToA(pledge._id);
      await fetchPledges();
    } catch (err) {
      alert(err.message || 'Failed to reset transfer status');
    }
  };

  return (
    <>
      <GirviPageShell
        title="All Pledges"
        subtitle="Pledge · List"
        chipLabel="Pledge"
        maxWidth="xl"
      >
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 1.5, md: 2 },
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Name, code, ticket, mobile, village, amount"
                sx={listFilterFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={handleDateChange('from')}
                InputLabelProps={{ shrink: true }}
                sx={listFilterFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={dateTo}
                onChange={handleDateChange('to')}
                InputLabelProps={{ shrink: true }}
                sx={listFilterFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<TransferIcon />}
                disabled={selectedIds.length === 0}
                onClick={() => openTransferDialog(selectedIds)}
                sx={{ bgcolor: '#1e3a5f', fontWeight: 700 }}
              >
                Transfer selected ({selectedIds.length})
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              overflow: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default', width: 42 }}>
                    <Checkbox
                      size="small"
                      checked={
                        filteredPledges.filter((p) => !p.transferredToA).length > 0 &&
                        filteredPledges
                          .filter((p) => !p.transferredToA)
                          .every((p) => selectedIds.includes(p._id))
                      }
                      indeterminate={
                        filteredPledges.some((p) => selectedIds.includes(p._id)) &&
                        !filteredPledges
                          .filter((p) => !p.transferredToA)
                          .every((p) => selectedIds.includes(p._id))
                      }
                      onChange={toggleSelectAllVisible}
                    />
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('date')}>
                    Date{sortMark('date')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('ticketNumber')}>
                    Ticket{sortMark('ticketNumber')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx}>Photos</TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('customer.code')}>
                    Code{sortMark('customer.code')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('customer.name')}>
                    Customer{sortMark('customer.name')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('loanDetails.principalAmount')} align="right">
                    Amount{sortMark('loanDetails.principalAmount')}
                  </TableCell>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default' }} align="right">
                    Interest till date
                  </TableCell>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default' }}>
                    Transfer
                  </TableCell>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default' }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPledges.length > 0 ? (
                  filteredPledges.map((pledge) => (
                    <TableRow
                      key={pledge._id}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(pledge._id)}
                          disabled={!!pledge.transferredToA}
                          onChange={() => toggleSelectPledge(pledge._id)}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: '#475569', fontWeight: 600 }}>
                        {formatDate(pledge.date)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={pledge.loanDetails?.ticketNo || 'N/A'}
                          sx={{
                            fontWeight: 800,
                            bgcolor: 'rgba(201,162,77,0.14)',
                            color: '#8a6d2f',
                            border: '1px solid rgba(201,162,77,0.35)',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          {(() => {
                            const photos = pledgeDisplayPhotos(pledge);
                            return (
                              <>
                                <PhotoThumb src={photos.clientPhoto} label="Client" size={36} />
                                <PhotoThumb src={photos.articlePhoto} label="Article" size={36} />
                              </>
                            );
                          })()}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {pledge.customer?.code || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {`${pledge.customer?.firstName || ''} ${pledge.customer?.lastName || ''}`.trim() || 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
                        {money(pledge.loanDetails?.principalAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#1e3a5f', whiteSpace: 'nowrap' }}>
                        {money(interestTillDate(pledge, new Date(), DEFAULT_DHARA))}
                      </TableCell>
                      <TableCell>
                        {pledge.transferredToA ? (
                          <Chip
                            size="small"
                            label={pledge.vpsATicketNo ? `A: ${pledge.vpsATicketNo}` : 'Transferred'}
                            onDelete={() => handleResetTransfer(pledge)}
                            sx={{ fontWeight: 700 }}
                          />
                        ) : (
                          <Button size="small" onClick={() => openTransferDialog([pledge._id])}>
                            Transfer
                          </Button>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <Tooltip title="Receipt">
                            <span>
                              <IconButton
                                onClick={() => setActionModal({ type: 'receipt', ticket: pledge.loanDetails?.ticketNo })}
                                disabled={pledge.status === 'RELEASED' || pledge.loanDetails?.released || !pledge.loanDetails?.ticketNo}
                                sx={listActionBtnSx}
                                size="small"
                              >
                                <ReceiptLongIcon sx={{ fontSize: 18, color: '#0b1220' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Release">
                            <span>
                              <IconButton
                                onClick={() => setActionModal({ type: 'release', ticket: pledge.loanDetails?.ticketNo })}
                                disabled={pledge.status === 'RELEASED' || pledge.loanDetails?.released || !pledge.loanDetails?.ticketNo}
                                sx={listActionBtnSx}
                                size="small"
                              >
                                <ReleaseIcon sx={{ fontSize: 18, color: '#a16207' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View">
                            <IconButton onClick={() => handleViewDetails(pledge)} sx={listActionBtnSx} size="small">
                              <VisibilityIcon sx={{ fontSize: 18, color: '#1e3a5f' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleEdit(pledge)} sx={listActionBtnSx} size="small">
                              <EditIcon sx={{ fontSize: 18, color: '#15803d' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleDelete(pledge)} sx={listActionBtnSx} size="small">
                              <DeleteIcon sx={{ fontSize: 18, color: '#b91c1c' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print">
                            <IconButton onClick={() => handlePrint(pledge)} sx={listActionBtnSx} size="small">
                              <PrintIcon sx={{ fontSize: 18, color: '#0369a1' }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 5, color: '#94a3b8' }}>
                      No pledges found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </GirviPageShell>

      <DetailDialog
        open={openDetails}
        onClose={handleCloseDetails}
        fitScreen
        maxWidth="lg"
        title={`Ticket ${selectedPledge?.loanDetails?.ticketNo || '—'}`}
        subtitle="Pledge details"
        status={selectedPledge?.status}
        statusColor={selectedPledge?.status === 'RELEASED' || selectedPledge?.status === 'closed' ? 'default' : 'success'}
        headerPhotos={
          (() => {
            const photos = pledgeDisplayPhotos(selectedPledge);
            return (
              <DetailHeaderPhotos
                compact
                size={44}
                clientPhoto={photos.clientPhoto}
                articlePhoto={photos.articlePhoto}
              />
            );
          })()
        }
      >
        {selectedPledge && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {(() => {
              const pair = pledgeInterestPair(selectedPledge);
              const options = pair.withinYear
                ? [{ id: 'simple', label: 'Simple int' }]
                : [
                    { id: 'compound', label: 'Compound int' },
                    { id: 'simple', label: 'Simple int' },
                  ];
              const shown = interestViewMode === 'compound' ? pair.compound : interestViewMode === 'simple' ? pair.simple : null;
              return (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
                    {options.map((option) => {
                      const active = interestViewMode === option.id;
                      return (
                        <Box
                          key={option.id}
                          onClick={() => setInterestViewMode(option.id)}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 1,
                            px: 1.25,
                            py: 0.45,
                            border: '1px solid',
                            borderColor: active ? '#0f172a' : '#dbe2ea',
                            bgcolor: active ? '#0f172a' : '#fff',
                            color: active ? '#fff' : '#475569',
                            fontSize: 12.5,
                            fontWeight: 750,
                          }}
                        >
                          {option.label}
                        </Box>
                      );
                    })}
                  </Box>
                  {shown != null && (
                    <Typography sx={{ textAlign: 'right', mt: 0.75, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                      Interest {money(shown)}
                    </Typography>
                  )}
                </Box>
              );
            })()}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1.2fr' },
                gap: 1.25,
              }}
            >
              <PledgeViewCard
                title="Customer"
                columns={2}
                items={[
                  { label: 'Code', value: selectedPledge.customer?.code || 'N/A' },
                  {
                    label: 'Name',
                    value: `${selectedPledge.customer?.firstName || ''} ${selectedPledge.customer?.lastName || ''}`.trim() || 'N/A',
                  },
                  { label: 'Date', value: formatDate(selectedPledge.date) },
                  {
                    label: 'Fingerprint',
                    value: selectedPledge.fingerprint?.template || selectedPledge.fingerprint?.image ? 'Yes' : 'No',
                  },
                ]}
              />
              <PledgeViewCard
                title="Loan"
                columns={3}
                items={[
                  { label: 'Principal', value: money(selectedPledge.loanDetails?.principalAmount) },
                  { label: 'Duration', value: `${selectedPledge.loanDetails?.duration || 'N/A'} months` },
                  { label: 'Rate', value: `${selectedPledge.loanDetails?.rateOfInterest || 'N/A'}%` },
                  ...(resolvePledgeDhara(selectedPledge, DEFAULT_DHARA)
                    ? [{ label: 'Dhara', value: formatDhara(resolvePledgeDhara(selectedPledge, DEFAULT_DHARA)) }]
                    : []),
                  { label: 'Paid excess', value: selectedPledge.loanDetails?.paidExcess ? 'Yes' : 'No' },
                ]}
              />
            </Box>

            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 1.5,
                border: '1px solid #e6ebf1',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1.25, py: 0.75, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#0f172a', borderLeft: '3px solid #c9a24d', pl: 0.75 }}>
                  Articles
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155', textAlign: 'right' }}>
                  {pledgeViewWeightLine(selectedPledge, masterArticles)}
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 220 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...listTableHeadSx, py: 0.75 }}>Description</TableCell>
                      <TableCell sx={{ ...listTableHeadSx, py: 0.75 }} align="right">Value</TableCell>
                      <TableCell sx={{ ...listTableHeadSx, py: 0.75 }} align="right">Gross</TableCell>
                      <TableCell sx={{ ...listTableHeadSx, py: 0.75 }} align="right">Net</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPledge.articles?.length ? selectedPledge.articles.map((article, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontWeight: 650, py: 0.6, fontSize: 13 }}>{article.description || 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: 13 }}>{money(article.valueTotal)}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: 13 }}>{formatWeightGrams3(article.grossWeight || article.grossWt)} g</TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontSize: 13 }}>{formatWeightGrams3(article.netWeight || article.netWt)} g</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: '#94a3b8', py: 2 }}>
                          No articles
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
      </DetailDialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this pledge? This action cannot be undone.
            {pledgeToDelete && (
              <Box sx={{ mt: 1.5 }}>
                <strong>Ticket No:</strong> {pledgeToDelete.loanDetails?.ticketNo || 'N/A'}<br />
                <strong>Customer:</strong> {pledgeToDelete.customer?.firstName || ''} {pledgeToDelete.customer?.lastName || ''}
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <TransferToADialog
        open={transferDialogOpen}
        pledgeIds={transferPledgeIds}
        onClose={() => {
          setTransferDialogOpen(false);
          setTransferPledgeIds([]);
        }}
        onSuccess={handleTransferSuccess}
      />

      <Dialog
        open={Boolean(actionModal)}
        onClose={() => {
          setActionModal(null);
          fetchPledges();
        }}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid rgba(212,175,106,0.45)',
            boxShadow: '0 24px 60px rgba(11,18,32,0.35)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(180deg, #0b1220 0%, #1a2d4a 100%)',
            color: '#f5e6c8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 1.25,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 11, letterSpacing: '0.14em', fontWeight: 800, color: 'rgba(245,230,200,0.7)' }}>
              {actionModal?.type === 'release' ? 'RELEASE' : 'RECEIPT'}
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>
              Ticket {actionModal?.ticket || ''}
            </Typography>
          </Box>
          <Button
            onClick={() => {
              setActionModal(null);
              fetchPledges();
            }}
            sx={{ color: '#f5e6c8', fontWeight: 800, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 1.25, bgcolor: '#f4f1ea' }}>
          {actionModal?.type === 'receipt' && (
            <CreateReceipt key={`receipt-${actionModal.ticket}`} embedded initialTicket={actionModal.ticket} />
          )}
          {actionModal?.type === 'release' && (
            <ReleasePage key={`release-${actionModal.ticket}`} embedded initialTicket={actionModal.ticket} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AllPledges;
